import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { FileService } from '../../../../core/services/file.service';
import type { PatchedMandatCommissaireAuxComptesPartialUpdateRequest } from '../models/cac-api.model';
import type { ModificationMandatCac, RattachementCacCreate, RemplacementCac } from '../models/cac.model';
import { MandatCommissaireComptesService } from './mandat-commissaire-comptes.service';

/**
 * Écritures du workflow de rattachement CAC — création initiale, modification du mandat et remplacement du porteur.
 *
 * Fait écran entre les modales et le contrat `/mandats-commissaires-comptes/` : les composants
 * ne manipulent que les modèles d'écran (`RattachementCacCreate`, `RemplacementCac`), la
 * traduction vers les DTO d'écriture vit ici. Service séparé de `CacApiService` (façade de
 * lecture/reconduction) pour maintenir chaque fichier sous le seuil `max-lines`.
 */
@Injectable({ providedIn: 'root' })
export class CacRattachementApiService {
    private readonly mandatService = inject(MandatCommissaireComptesService);
    private readonly fileService = inject(FileService);

    /**
     * Rattachement initial d'un CAC — `POST /mandats-commissaires-comptes/`.
     *
     * L'acte de nomination est déposé au préalable sur `/files/`, typé par le type d'acte, comme
     * le fait la Fiche 360°. C'est lui qui permet au serveur d'appliquer la durée du premier
     * mandat (2 ans sur procès-verbal d'assemblée générale constitutive). Le serveur ne supprimant
     * pas les fichiers (DELETE non routé), une création en échec laisse l'acte déposé sans lien.
     */
    createRattachement(payload: RattachementCacCreate): Observable<void> {
        const depot$: Observable<string | undefined> = payload.acte
            ? this.fileService.upload(payload.acte, payload.type_acte_nomination_id, { societeId: payload.societe_id }).pipe(map((f) => f.id))
            : of(undefined);
        return depot$.pipe(
            switchMap((acteId) =>
                this.mandatService.create({
                    // Chaque rôle est porté par une personne physique OU un cabinet : la clé est omise
                    // plutôt qu'envoyée vide (la chaîne vide est refusée par le serveur).
                    ...(payload.titulaire.personne_physique_id ? { personne_physique_titulaire_id: payload.titulaire.personne_physique_id } : {}),
                    ...(payload.titulaire.cabinet_id ? { cabinet_titulaire_id: payload.titulaire.cabinet_id } : {}),
                    type_mandataire_titulaire_id: payload.titulaire.type_mandataire_id,
                    ...(payload.suppleant?.personne_physique_id ? { personne_physique_suppleant_id: payload.suppleant.personne_physique_id } : {}),
                    ...(payload.suppleant?.cabinet_id ? { cabinet_suppleant_id: payload.suppleant.cabinet_id } : {}),
                    ...(payload.suppleant?.type_mandataire_id ? { type_mandataire_suppleant_id: payload.suppleant.type_mandataire_id } : {}),
                    ...(acteId ? { acte_nomination_id: acteId } : {}),
                    societe_id: payload.societe_id,
                    ...(payload.type_acte_nomination_id ? { type_acte_nomination_id: payload.type_acte_nomination_id } : {}),
                    date_designation: payload.date_designation,
                    reference_acte_nomination: payload.reference_acte_nomination,
                }),
            ),
            map(() => undefined),
        );
    }

    /**
     * Modification d'un mandat en cours — `PATCH /mandats-commissaires-comptes/{id}/`, champs modifiés seulement.
     *
     * Un nouvel acte est d'abord déposé sur /files/, typé par le type d'acte comme à la création
     * (Fiche 360°). Le serveur ne supprimant pas les fichiers (DELETE non routé), un PATCH en échec
     * laisse l'acte déposé sans lien.
     */
    modifierMandat(modification: ModificationMandatCac, typeActeCourantId: string): Observable<void> {
        const { mandat_id: mandatId, societe_id: societeId, acte, suppleant, ...champs } = modification;
        const depot$: Observable<string | undefined> = acte
            ? this.fileService.upload(acte, champs.type_acte_nomination_id ?? typeActeCourantId, { societeId }).pipe(map((f) => f.id))
            : of(undefined);
        return depot$.pipe(
            switchMap((acteId) => {
                const payload: PatchedMandatCommissaireAuxComptesPartialUpdateRequest = {
                    ...champs,
                    ...(suppleant?.personne_physique_id ? { personne_physique_suppleant_id: suppleant.personne_physique_id } : {}),
                    ...(suppleant?.cabinet_id ? { cabinet_suppleant_id: suppleant.cabinet_id } : {}),
                    ...(suppleant?.type_mandataire_id ? { type_mandataire_suppleant_id: suppleant.type_mandataire_id } : {}),
                    ...(acteId ? { acte_nomination_id: acteId } : {}),
                };
                return this.mandatService.partialUpdate(mandatId, payload);
            }),
            map((): void => undefined),
        );
    }

    /**
     * Remplacement d'un rôle en cours de mandat — PATCH du remplaçant, de la prise d'effet et du
     * motif. Le champ de remplaçant dépend du rôle visé ; prise d'effet et motif sont communs à
     * la paire, le serveur n'en portant qu'un couple.
     */
    remplacerCac(payload: RemplacementCac): Observable<void> {
        const remplacant =
            payload.role === 'SUPPLEANT'
                ? { personne_physique_suppleant_remplacant_id: payload.personne_physique_remplacant_id }
                : { personne_physique_titulaire_remplacant_id: payload.personne_physique_remplacant_id };
        return this.mandatService
            .partialUpdate(payload.mandat_id, {
                ...remplacant,
                motif_modification: payload.motif,
                date_debut_remplacement: payload.date_debut_remplacement,
            })
            .pipe(map(() => undefined));
    }
}
